/**
 * FeatureGate — Wrapper component for gated features
 *
 * Usage:
 *   <FeatureGate requiredTier="free">   — requires login (any tier)
 *   <FeatureGate requiredTier="pro">    — requires pro subscription
 *   <FeatureGate requiredTier="premium"> — requires premium subscription
 *
 * If user isn't logged in or doesn't have the tier, shows a prompt instead.
 */

import { useState } from 'react'
import { Lock, Zap } from 'lucide-react'
import { useAuth, hasTierAccess } from './AuthProvider'
import AuthModal from './AuthModal'

const TIER_LABELS = {
  free: 'Free',
  pro: 'Pro',
  premium: 'Premium',
}

export default function FeatureGate({ requiredTier = 'free', children, fallbackMessage }) {
  const { isAuthenticated, userTier } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)

  // If user is authenticated and has the required tier, render children
  if (isAuthenticated && hasTierAccess(userTier, requiredTier)) {
    return <>{children}</>
  }

  // Not logged in
  if (!isAuthenticated) {
    return (
      <>
        <div className="auth-gate">
          <Lock size={18} className="auth-gate-icon" />
          <span className="auth-gate-text">
            {fallbackMessage || 'Sign in to use this feature'}
          </span>
          <button
            className="auth-gate-btn"
            onClick={() => setShowAuthModal(true)}
          >
            Sign In
          </button>
        </div>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialTab="signin"
        />
      </>
    )
  }

  // Logged in but wrong tier
  return (
    <div className="auth-gate auth-gate-upgrade">
      <Zap size={18} className="auth-gate-icon" />
      <span className="auth-gate-text">
        Upgrade to {TIER_LABELS[requiredTier] || requiredTier} to unlock this feature
      </span>
      <button className="auth-gate-btn auth-gate-btn-upgrade" disabled>
        Coming Soon
      </button>
    </div>
  )
}

/**
 * Hook to check feature access without rendering a gate UI.
 * Returns { hasAccess, isAuthenticated, userTier, requiredTier }
 */
export function useFeatureAccess(requiredTier = 'free') {
  const { isAuthenticated, userTier } = useAuth()
  const hasAccess = isAuthenticated && hasTierAccess(userTier, requiredTier)

  return {
    hasAccess,
    isAuthenticated,
    userTier,
    requiredTier,
  }
}
